import type { EnvironmentThreadShell } from "@t3tools/client-runtime/state/models";
import type { MessageKey } from "~/i18n";
import { normalizeSearchText } from "../CommandPalette.logic";

export type SidebarSearchResultKind =
  | "thread"
  | "page"
  | "agent"
  | "member"
  | "vault-file"
  | "knowledge";

export type SidebarSearchThreadResult = {
  readonly kind: "thread";
  readonly id: string;
  readonly thread: EnvironmentThreadShell;
  readonly title: string;
  readonly subtitle: string | null;
  readonly searchTerms: ReadonlyArray<string>;
};

export type SidebarSearchPageResult = {
  readonly kind: "page";
  readonly id: string;
  readonly route:
    | "/activities"
    | "/team"
    | "/organization"
    | "/agents"
    | "/knowledge"
    | "/vault"
    | "/settings";
  readonly title: string;
  readonly searchTerms: ReadonlyArray<string>;
};

export type SidebarSearchAgentResult = {
  readonly kind: "agent";
  readonly id: string;
  readonly agentId: string;
  readonly title: string;
  readonly subtitle: string;
  readonly imageSrc: string;
  readonly searchTerms: ReadonlyArray<string>;
};

export type SidebarSearchMemberResult = {
  readonly kind: "member";
  readonly id: string;
  readonly memberKey: string;
  readonly title: string;
  readonly subtitle: string;
  readonly searchTerms: ReadonlyArray<string>;
};

export type SidebarSearchVaultFileResult = {
  readonly kind: "vault-file";
  readonly id: string;
  readonly itemId: string;
  readonly title: string;
  readonly subtitle: string;
  readonly searchTerms: ReadonlyArray<string>;
};

export type SidebarSearchKnowledgeResult = {
  readonly kind: "knowledge";
  readonly id: string;
  readonly tab: "profile" | "behavior" | "preferences";
  readonly title: string;
  readonly searchTerms: ReadonlyArray<string>;
};

export type SidebarSearchResult =
  | SidebarSearchThreadResult
  | SidebarSearchPageResult
  | SidebarSearchAgentResult
  | SidebarSearchMemberResult
  | SidebarSearchVaultFileResult
  | SidebarSearchKnowledgeResult;

export type SidebarSearchGroup = {
  readonly kind: SidebarSearchResultKind;
  readonly labelKey: MessageKey;
  readonly results: ReadonlyArray<SidebarSearchResult>;
};

export type SidebarSearchCatalogAgent = {
  readonly id: string;
  readonly title: string;
  readonly subtitle: string;
  readonly imageSrc: string;
  readonly searchTerms: ReadonlyArray<string>;
};

export type SidebarSearchCatalogMember = {
  readonly key: string;
  readonly title: string;
  readonly subtitle: string;
  readonly searchTerms: ReadonlyArray<string>;
};

export type SidebarSearchCatalogVaultFile = {
  readonly id: string;
  readonly title: string;
  readonly subtitle: string;
  readonly searchTerms: ReadonlyArray<string>;
};

export type SidebarSearchCatalogKnowledgeTab = {
  readonly tab: "profile" | "behavior" | "preferences";
  readonly title: string;
  readonly searchTerms: ReadonlyArray<string>;
};

export type SidebarSearchCatalogPage = {
  readonly route: SidebarSearchPageResult["route"];
  readonly title: string;
  readonly searchTerms: ReadonlyArray<string>;
};

export type SidebarSearchCatalog = {
  readonly agents: ReadonlyArray<SidebarSearchCatalogAgent>;
  readonly members: ReadonlyArray<SidebarSearchCatalogMember>;
  readonly vaultFiles: ReadonlyArray<SidebarSearchCatalogVaultFile>;
  readonly knowledgeTabs: ReadonlyArray<SidebarSearchCatalogKnowledgeTab>;
  readonly pages: ReadonlyArray<SidebarSearchCatalogPage>;
};

const GROUP_ORDER: ReadonlyArray<SidebarSearchResultKind> = [
  "thread",
  "page",
  "agent",
  "member",
  "vault-file",
  "knowledge",
];

const GROUP_LABEL_KEYS = {
  thread: "sidebar.searchGroup.threads",
  page: "sidebar.searchGroup.pages",
  agent: "sidebar.searchGroup.agents",
  member: "sidebar.searchGroup.members",
  "vault-file": "sidebar.searchGroup.vault",
  knowledge: "sidebar.searchGroup.knowledge",
} as const satisfies Record<SidebarSearchResultKind, MessageKey>;

const MAX_RESULTS_PER_GROUP = 5;

function rankSearchFieldMatch(field: string, normalizedQuery: string): number {
  const normalizedField = normalizeSearchText(field);
  if (normalizedField.length === 0 || !normalizedField.includes(normalizedQuery)) {
    return Number.NEGATIVE_INFINITY;
  }
  if (normalizedField === normalizedQuery) return 3;
  if (normalizedField.startsWith(normalizedQuery)) return 2;
  return 1;
}

function rankSearchTerms(searchTerms: ReadonlyArray<string>, normalizedQuery: string): number {
  let best = 0;
  for (const [index, term] of searchTerms.entries()) {
    const fieldRank = rankSearchFieldMatch(term, normalizedQuery);
    if (fieldRank === Number.NEGATIVE_INFINITY) continue;
    best = Math.max(best, 1_000 - index * 100 + fieldRank);
  }
  return best;
}

function matchesQuery(searchTerms: ReadonlyArray<string>, normalizedQuery: string): boolean {
  const haystack = normalizeSearchText(searchTerms.join(" "));
  return haystack.includes(normalizedQuery);
}

function filterRankedResults<T extends { readonly searchTerms: ReadonlyArray<string> }>(
  items: ReadonlyArray<T>,
  normalizedQuery: string,
  limit: number,
): T[] {
  return items
    .flatMap((item, index) => {
      if (!matchesQuery(item.searchTerms, normalizedQuery)) return [];
      return [{ item, index, rank: rankSearchTerms(item.searchTerms, normalizedQuery) }];
    })
    .toSorted((left, right) => right.rank - left.rank || left.index - right.index)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function buildSidebarUniversalSearch(input: {
  readonly query: string;
  readonly threads: ReadonlyArray<EnvironmentThreadShell>;
  readonly projectTitleByKey: ReadonlyMap<string, string>;
  readonly catalog: SidebarSearchCatalog;
}): {
  readonly groups: ReadonlyArray<SidebarSearchGroup>;
  readonly flatResults: ReadonlyArray<SidebarSearchResult>;
} {
  const normalizedQuery = normalizeSearchText(input.query);
  if (normalizedQuery.length === 0) {
    return { groups: [], flatResults: [] };
  }

  const threadResults: SidebarSearchThreadResult[] = filterRankedResults(
    input.threads.map((thread) => {
      const projectTitle =
        input.projectTitleByKey.get(`${thread.environmentId}:${thread.projectId}`) ?? null;
      return {
        kind: "thread" as const,
        id: `thread:${thread.environmentId}:${thread.id}`,
        thread,
        title: thread.title,
        subtitle: null,
        searchTerms: [thread.title, projectTitle ?? "", thread.branch ?? ""],
      };
    }),
    normalizedQuery,
    MAX_RESULTS_PER_GROUP,
  );

  const pageResults: SidebarSearchPageResult[] = filterRankedResults(
    input.catalog.pages.map((page) => ({
      kind: "page" as const,
      id: `page:${page.route}`,
      route: page.route,
      title: page.title,
      searchTerms: page.searchTerms,
    })),
    normalizedQuery,
    MAX_RESULTS_PER_GROUP,
  );

  const agentResults: SidebarSearchAgentResult[] = filterRankedResults(
    input.catalog.agents.map((agent) => ({
      kind: "agent" as const,
      id: `agent:${agent.id}`,
      agentId: agent.id,
      title: agent.title,
      subtitle: agent.subtitle,
      imageSrc: agent.imageSrc,
      searchTerms: agent.searchTerms,
    })),
    normalizedQuery,
    MAX_RESULTS_PER_GROUP,
  );

  const memberResults: SidebarSearchMemberResult[] = filterRankedResults(
    input.catalog.members.map((member) => ({
      kind: "member" as const,
      id: `member:${member.key}`,
      memberKey: member.key,
      title: member.title,
      subtitle: member.subtitle,
      searchTerms: member.searchTerms,
    })),
    normalizedQuery,
    MAX_RESULTS_PER_GROUP,
  );

  const vaultResults: SidebarSearchVaultFileResult[] = filterRankedResults(
    input.catalog.vaultFiles.map((file) => ({
      kind: "vault-file" as const,
      id: `vault:${file.id}`,
      itemId: file.id,
      title: file.title,
      subtitle: file.subtitle,
      searchTerms: file.searchTerms,
    })),
    normalizedQuery,
    MAX_RESULTS_PER_GROUP,
  );

  const knowledgeResults: SidebarSearchKnowledgeResult[] = filterRankedResults(
    input.catalog.knowledgeTabs.map((tab) => ({
      kind: "knowledge" as const,
      id: `knowledge:${tab.tab}`,
      tab: tab.tab,
      title: tab.title,
      searchTerms: tab.searchTerms,
    })),
    normalizedQuery,
    MAX_RESULTS_PER_GROUP,
  );

  const resultsByKind: Record<SidebarSearchResultKind, ReadonlyArray<SidebarSearchResult>> = {
    thread: threadResults,
    page: pageResults,
    agent: agentResults,
    member: memberResults,
    "vault-file": vaultResults,
    knowledge: knowledgeResults,
  };

  const groups = GROUP_ORDER.flatMap((kind) => {
    const results = resultsByKind[kind];
    if (results.length === 0) return [];
    return [{ kind, labelKey: GROUP_LABEL_KEYS[kind], results }];
  });

  return {
    groups,
    flatResults: groups.flatMap((group) => group.results),
  };
}
